import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryGetInquiryTemplate } from 'containers/ProductRegist/components/InformationRegist/graphQL/hooks/useQueryGetInquiryTemplate';
import { useMutationProductItems } from 'containers/ProductRegist/graphQL/hooks/useMutationProductItems';
import { useQueryProduct } from 'containers/ProductRegist/graphQL/hooks/useQueryProduct';
import { ProductParamInput } from 'containers/ProductRegist/hooks/types';
import { Product, ProductState } from 'types/api';
import { ConvertingProductParamInput } from 'utils/ConvertingProductParamInput';
import { useQueryGetProductScheduleTerm } from 'containers/ProductRegist/graphQL/hooks/useQueryGetProductScheduleTerm';
import { useMutationSaveProductRefundPolicy } from 'containers/ProductRegist/graphQL/hooks/useMutationSaveProductRefundPolicy';
import { useMutationProductStatus } from 'containers/ProductRegist/graphQL/hooks/useMutationProductStatus';
import { errorHandler } from 'utils/ErrorHandler';
import { useMutationRequestInspection } from 'containers/ProductRegist/graphQL/hooks/useMutationRequestInspection';
import { useQueryGetRefundPolicyByFilter } from 'containers/ProductRegist/graphQL/hooks/useQueryGetRefundPolicyByFilter';
export interface ModifyValue {
  modifyStatus: string;
  isModify: boolean;
}

export const useProductDetailHooks = () => {
  const params = useParams<{ id: string }>();
  const convertingProductParamInput = new ConvertingProductParamInput();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isUpdatingRef = useRef<boolean>(false);
  const appliedDataRef = useRef<any>(null);

  const { data, handleGetProduct, handleRefetchProduct } = useQueryProduct();
  const {
    scheduleList,
    handleGetProductScheduleTerm,
    handleRefetchScheduleTerm,
  } = useQueryGetProductScheduleTerm();
  const {
    inquiryList,
    handelGetInquiryTemplate,
    handleRefetchInquiryTemplate,
  } = useQueryGetInquiryTemplate();
  const { saveProduct, loading: isSaveProductLoading } =
    useMutationProductItems();
  const { handleSaveProductRefundPolicy } =
    useMutationSaveProductRefundPolicy();
  const { handleOnChangeProductStatus } = useMutationProductStatus(params.id);
  const { handleRequestInspection, isRequestInspectionLoading } =
    useMutationRequestInspection();
  const { handleGetRefundPolicyByFilter } = useQueryGetRefundPolicyByFilter();

  const [menuType, setMenuType] = useState<string>('detail');
  const [modifyValue, setModifyValue] = useState<ModifyValue>({
    modifyStatus: window.innerWidth < 768 ? '' : 'image',
    isModify: false,
  });
  const [productParamInput, setProductParamInput] =
    useState<ProductParamInput>();

  const [isOpenConfirmDialog, setIsOpenConfirmDialog] =
    useState<boolean>(false);
  const [isOpenCloseProductDetail, setIsOpenCloseProductDetail] =
    useState<boolean>(false);

  const handleSetMenuType = (type: string) => {
    if (type === '상세 페이지') {
      setMenuType('detail');

      return;
    }

    setMenuType('option');
  };

  const handleOnChangeProductParamInput = (key: string, value: any) => {
    setProductParamInput({ ...productParamInput, [key]: value });
  };

  const handleSetProductStatus = (status: ProductState) => {
    switch (status) {
      case ProductState.INSPECTING: {
        handleOnChangeProductStatus('cancelInspection');

        return;
      }
      case ProductState.READY: {
        handleOnChangeProductStatus('open');

        return;
      }
      case ProductState.SALE: {
        handleOnChangeProductStatus('pause');

        return;
      }
      case ProductState.PAUSED: {
        handleOnChangeProductStatus('open');

        return;
      }
      case ProductState.SOLD_OUT: {
        handleOnChangeProductStatus('open');

        return;
      }
      case ProductState.SUSPENDED: {
        return '고객센터에 문의할까요?';
      }
      case ProductState.EDITING: {
        handleRequestInspection(params.id, () => {
          window.location.reload();
          // handleRefetchProduct(params.id);
        });

        return;
      }
    }
  };

  /* 상품 수정 버튼 클릭시 호출 */
  const handleUpdateProduct = async () => {
    if (isLoading || isSaveProductLoading || isUpdatingRef.current) {
      return;
    }
    isUpdatingRef.current = true;
    setIsLoading(true);

    try {
      const convertProductParam =
        convertingProductParamInput.convertProductParam(
          productParamInput,
          scheduleList,
        );

      const response = await saveProduct({
        variables: { id: params.id, param: convertProductParam },
      });

      if (response) {
        window.showToast('수정사항이 적용되었습니다', 'success');

        setModifyValue({ ...modifyValue, isModify: false });

        handleGetRefundPolicyByFilter(convertProductParam, refundPolicyId => {
          handleSaveProductRefundPolicy(params.id, refundPolicyId);
        });

        handleRefetchProduct(params.id);
        handleRefetchScheduleTerm();
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      isUpdatingRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGetProduct(params.id);
  }, []);

  useEffect(() => {
    if (data && scheduleList) {
      // 적용(스테이징) 후 저장 전이면 서버 값으로 리셋하지 않음 — 수정사항 유실 방지
      if (modifyValue.isModify) {
        if (appliedDataRef.current !== data) {
          console.warn(
            '[ProductDetail] 미저장 수정사항이 있어 서버 데이터 리셋을 건너뜀',
          );
        }

        return;
      }

      appliedDataRef.current = data;

      const productInfo: Product = data.product.product;
      const paramInput = convertingProductParamInput.convertProductParamInput(
        productInfo,
        scheduleList,
      );

      setProductParamInput(paramInput);

      if (productInfo.inquiryTemplate !== null) {
        handelGetInquiryTemplate(productInfo.inquiryTemplate.id);
      }
    }
  }, [data, scheduleList, modifyValue.isModify]);

  return {
    productInfo: data?.product.product as Product,
    menuType,
    productParamInput,
    modifyValue,
    inquiryList,
    isOpenConfirmDialog,
    isOpenCloseProductDetail,
    isRequestInspectionLoading,
    setIsOpenCloseProductDetail,
    setIsOpenConfirmDialog,
    setModifyValue,
    setProductParamInput,
    handleOnChangeProductParamInput,
    handleSetProductStatus,
    handleSetMenuType,
    handleUpdateProduct,
    handleGetProductScheduleTerm,
    handelGetInquiryTemplate,
    handleRefetchInquiryTemplate,
    handleRefetchProduct,
  };
};
